import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  BUXUS_APP_DISPLAY_VERSION,
  BUXUS_SITE_CONDENSED_LABEL,
  BUXUS_SITE_URL,
  classifyThrownFailure,
  outcomeFailureProps,
  outcomeSuccessProps,
  pageViewedScreenProps,
} from '@buxus/shared'
import './LoginScreen.css'
import { abpAdapter } from './platform/abp/adapter'
import { useAbp } from './platform/abp/react'
import { EventTypes, trackEvent } from './platform/analytics/analytics'
import {
  clearPendingLoginAnalytics,
  storePendingLoginAnalytics,
} from './platform/analytics/loginAnalytics'
import {
  friendlyLoginError,
  LOGIN_ERROR_MODAL_TITLE,
  LOGIN_PROFILE_ERROR_STORAGE_KEY,
} from './platform/auth/loginErrorMessage'
import { beginOidcLogin } from './platform/auth/oidc'
import { reportReliabilityFailure } from './platform/errors/webFailureReporting'
import { showAppErrorModal } from './platform/ui/appErrorModal'
import { REGISTER_PATH, safeLoginReturnTo } from './routing/pathPolicy'
import buxusLogoLogin from './assets/buxus-logo-login.png'

type Props = {
  onSignedIn: () => void
}

type LoginLocationState = {
  returnTo?: string
}

function emailLooksValid(raw: string): boolean {
  const t = raw.trim()
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)
}

export function LoginScreen({ onSignedIn }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const { ready: abpReady, isAuthenticated } = useAbp()
  const [email, setEmail] = useState('')
  const [operatorMode, setOperatorMode] = useState(false)
  const [busy, setBusy] = useState(false)
  const loginPageViewSentRef = useRef(false)

  useEffect(() => {
    if (!abpReady || isAuthenticated || loginPageViewSentRef.current) return
    loginPageViewSentRef.current = true
    const screenProps = pageViewedScreenProps('login')
    trackEvent(EventTypes.PAGE_VIEWED, {
      ...screenProps,
      routePath: location.pathname,
    })
  }, [abpReady, isAuthenticated, location.pathname])

  useEffect(() => {
    if (!abpReady || !isAuthenticated) return
    onSignedIn()
    const returnTo = safeLoginReturnTo((location.state as LoginLocationState | null)?.returnTo)
    navigate(returnTo, { replace: true })
  }, [abpReady, isAuthenticated, location.state, navigate, onSignedIn])

  useEffect(() => {
    try {
      const blocked = sessionStorage.getItem(LOGIN_PROFILE_ERROR_STORAGE_KEY)
      if (blocked) {
        showAppErrorModal({ title: LOGIN_ERROR_MODAL_TITLE, message: blocked })
        sessionStorage.removeItem(LOGIN_PROFILE_ERROR_STORAGE_KEY)
      }
    } catch {
      /* ignore */
    }
  }, [])

  const trimmed = email.trim()
  const operatorEmailOk = trimmed.length > 0 && emailLooksValid(trimmed)
  const operatorLoginDisabled = busy || !operatorEmailOk

  const showLoginError = (err: unknown) => {
    showAppErrorModal({
      title: LOGIN_ERROR_MODAL_TITLE,
      message: friendlyLoginError(err),
    })
  }

  const runUserLogin = async () => {
    if (busy) return
    trackEvent(
      EventTypes.USER_LOGIN_ATTEMPTED,
      {
        email: '',
        mode: 'user',
        isValid: true,
      },
      { sendImmediately: true },
    )
    setBusy(true)
    try {
      storePendingLoginAnalytics({ email: '', mode: 'user' })
      await beginOidcLogin()
    } catch (err: unknown) {
      clearPendingLoginAnalytics()
      const classified = classifyThrownFailure(err)
      reportReliabilityFailure(err, {
        context: 'userLoginOidc',
        failureKind: classified.failureKind,
        analyticsEvent: EventTypes.USER_LOGIN_FLOW_FAILED,
        analyticsProps: {
          email: '',
          mode: 'user',
          ...outcomeFailureProps({
            errorMessage: classified.errorMessage,
            failureKind: classified.failureKind,
          }),
        },
      })
      showLoginError(err)
    } finally {
      setBusy(false)
    }
  }

  const runOperatorLogin = async (e?: FormEvent) => {
    e?.preventDefault()
    if (!operatorMode || busy) return
    if (!operatorEmailOk) {
      trackEvent(EventTypes.FORM_VALIDATION_FAILED, {
        field: 'email',
        message: 'Enter a valid email address.',
        mode: 'operator',
      })
      return
    }
    trackEvent(
      EventTypes.USER_LOGIN_ATTEMPTED,
      {
        email: trimmed,
        mode: 'operator',
        isValid: true,
      },
      { sendImmediately: true },
    )
    setBusy(true)
    try {
      const disco = await abpAdapter.discoverLoginMethod(trimmed)
      trackEvent(EventTypes.LOGIN_METHOD_DISCOVERED, {
        email: trimmed,
        result: disco.result ?? 'unknown',
        clientId: disco.clientId,
        provider: disco.provider,
        tenantName: disco.tenantName,
        ...outcomeSuccessProps(),
      })
      storePendingLoginAnalytics({ email: trimmed, mode: 'operator' })
      await beginOidcLogin({ loginHint: trimmed })
    } catch (err: unknown) {
      clearPendingLoginAnalytics()
      const classified = classifyThrownFailure(err)
      reportReliabilityFailure(err, {
        context: 'loginMethodDiscover',
        failureKind: classified.failureKind,
        analyticsEvent: EventTypes.LOGIN_METHOD_DISCOVER_FAILED,
        analyticsProps: {
          email: trimmed,
          ...outcomeFailureProps({
            errorMessage: classified.errorMessage,
            failureKind: classified.failureKind,
          }),
        },
      })
      showLoginError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="app-login">
      <div className="app-login-card">
        <div className="app-login-brand">
          <img
            className="app-login-logo-img"
            src={buxusLogoLogin}
            alt="Buxus"
            width={320}
            height={120}
          />
        </div>

        <div className="app-login-actions-stack">
          {!operatorMode ? (
            <div className="app-login-btn-group">
              <button
                type="button"
                className="app-login-btn app-login-btn-primary"
                disabled={busy}
                onClick={() => void runUserLogin()}
              >
                <span className="app-login-btn-inner">
                  <span>LOG IN</span>
                  {busy ? <span className="app-login-btn-spinner" aria-hidden /> : null}
                </span>
              </button>
              {abpReady && isAuthenticated ? (
                <span
                  className="app-login-btn app-login-btn-register app-login-btn-register-disabled"
                  title="Sign out to register a different Buxus account."
                >
                  REGISTER
                </span>
              ) : (
                <Link to={REGISTER_PATH} className="app-login-btn app-login-btn-register">
                  REGISTER
                </Link>
              )}
            </div>
          ) : (
            <form className="app-login-form" onSubmit={(e) => void runOperatorLogin(e)}>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="Email address"
                disabled={busy}
                className="app-login-input"
                aria-label="Email address"
              />
              {trimmed.length > 0 && !emailLooksValid(trimmed) ? (
                <p className="app-login-validation">Enter a valid email address.</p>
              ) : null}
              <button
                type="submit"
                className="app-login-btn app-login-btn-primary"
                disabled={operatorLoginDisabled}
              >
                <span className="app-login-btn-inner">
                  <span>LOG IN</span>
                  {busy ? <span className="app-login-btn-spinner" aria-hidden /> : null}
                </span>
              </button>
            </form>
          )}
        </div>

        <footer className="app-login-footer">
          <p>
            For assistance contact{' '}
            <a href="mailto:help@buxus.io">help@buxus.io</a> or call{' '}
            <a href="tel:8448883318">844-888-3318</a>.
          </p>
          <p className="app-login-footer-meta">
            v{BUXUS_APP_DISPLAY_VERSION} - Learn more at{' '}
            <a href={BUXUS_SITE_URL} target="_blank" rel="noopener noreferrer">
              {BUXUS_SITE_CONDENSED_LABEL}
            </a>
            .
          </p>
        </footer>

        <button
          type="button"
          className="app-login-mode-toggle"
          disabled={busy}
          onClick={() => {
            setOperatorMode((prev) => {
              const next = !prev
              trackEvent(EventTypes.AUTH_MODE_TOGGLED, {
                newMode: next ? 'operator' : 'user',
              })
              return next
            })
            setEmail('')
          }}
        >
          {operatorMode ? 'User Login' : 'Operator Login'}
        </button>
      </div>
    </div>
  )
}
